import { Paper, Group, Text, Button, Menu, ActionIcon } from '@mantine/core';
import {
  IconPrinter,
  IconClipboardPlus,
  IconEdit,
  IconDots,
  IconX,
  IconTrash,
} from '@tabler/icons-react';
import { usePermissions } from '@/hooks/usePermissions';
import classes from './FloatingBulkActionBar.module.css';

interface FloatingBulkActionBarProps {
  selectedCount: number;
  onPrintQR: () => void;
  onAddToAudit: () => void;
  onBulkUpdate: () => void;
  onBulkDelete: () => void;
  onClearSelection: () => void;
}

export function FloatingBulkActionBar({
  selectedCount,
  onPrintQR,
  onAddToAudit,
  onBulkUpdate,
  onBulkDelete,
  onClearSelection,
}: FloatingBulkActionBarProps) {
  const { can } = usePermissions();

  if (selectedCount === 0) return null;

  return (
    <Paper
      shadow="lg"
      radius="md"
      p="md"
      className={classes.floatingBar}
      withBorder
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xl">
          <Group gap="xs">
            <div className={classes.checkmark}>✓</div>
            <Text fw={500} size="sm">
              {selectedCount} selected
            </Text>
          </Group>

          <Group gap="xs">
            {can('assets', 'write') && (
              <>
                <Button
                  size="sm"
                  variant="light"
                  color="grape"
                  leftSection={<IconPrinter size={16} />}
                  onClick={onPrintQR}
                >
                  Print QR
                </Button>

                <Button
                  size="sm"
                  variant="light"
                  color="green"
                  leftSection={<IconClipboardPlus size={16} />}
                  onClick={onAddToAudit}
                >
                  Add to Audit
                </Button>

                <Button
                  size="sm"
                  variant="light"
                  color="blue"
                  leftSection={<IconEdit size={16} />}
                  onClick={onBulkUpdate}
                >
                  Update
                </Button>

                <Menu position="bottom-end" shadow="md">
                  <Menu.Target>
                    <Button
                      size="sm"
                      variant="subtle"
                      color="gray"
                      leftSection={<IconDots size={16} />}
                    >
                      More
                    </Button>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Label>Additional Actions</Menu.Label>
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      onClick={onBulkDelete}
                      color="red"
                    >
                      Delete {selectedCount} Asset{selectedCount > 1 ? 's' : ''}
                    </Menu.Item>
                    {/* Add more future bulk actions here */}
                  </Menu.Dropdown>
                </Menu>
              </>
            )}
          </Group>
        </Group>

        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClearSelection}
          aria-label="Clear selection"
          size="lg"
        >
          <IconX size={18} />
        </ActionIcon>
      </Group>
    </Paper>
  );
}
